import type { MenuViewKey, ViewKey } from './role';

/**
 * Which screens a build of the app is told about. The lab's request of
 * 12 October 2026, which added the Dashboard, and the API's HomeScreens.
 *
 * A screen is a word the app on a device has to know: it has a path, a title
 * and a place in the menu there. A build from before a screen existed knows
 * none of them, and a session that named the screen as its landing screen
 * would leave that build with nowhere to go. A home screen app can run
 * yesterday's build for days. So a build says which of the newer screens it
 * can draw, in a header on every call, and a newer screen it does not name is
 * left out of the session it is sent: its screens and its landing screen are
 * then exactly what they were before the screen existed.
 *
 * What the server allows the caller is not touched. This is only what the app
 * is told.
 */

/** The request header in which a build names the newer screens it knows, comma separated. */
export const SCREENS_HEADER = 'X-Ceramica-Screens';

/**
 * The screens added since builds began to say which they know. A screen added
 * from now on goes here on the day this build can draw it, and into the API's
 * list of the same name.
 */
export const NEWER_SCREENS: readonly MenuViewKey[] = ['dashboard'];

/** What this build sends: every newer screen it can draw. */
export const SCREENS_HEADER_VALUE = NEWER_SCREENS.join(',');

/** The screens a header's value names: "dashboard, other". None for no header. */
export function namedScreens(header: string | null | undefined): string[] {
  if (header === null || header === undefined) return [];
  return [
    ...new Set(
      header
        .split(',')
        .map((name) => name.trim())
        .filter((name) => name !== ''),
    ),
  ];
}

/** A person's screens as a build that names these newer ones is told them. */
export function screensKnownTo(views: readonly ViewKey[], named: readonly string[]): ViewKey[] {
  return views.filter(
    (view) => !(NEWER_SCREENS as readonly ViewKey[]).includes(view) || named.includes(view),
  );
}
